import { api } from "@/features/utils/fetch-server";
import type { Connector } from "../types";

export interface MetaSettings {
  app_id: string;
  graph_version: string;
  /** Embedded Signup configuration id (WhatsApp). */
  embedded_signup_config_id: string;
  /** False when the platform has no Meta app configured. */
  enabled: boolean;
}

export interface MetaPage {
  id: string;
  name: string;
  instagram?: { id: string; username?: string };
}

export interface WhatsAppSignupPayload {
  /** What the login callback handed back. */
  handle: string;
  /** What Embedded Signup reported, when its message reached the page. */
  waba_id?: string;
  phone_number_id?: string;
  coexistence?: boolean;
  agent_id?: string;
}

/** A WhatsApp number the Meta login gave access to. */
export interface WhatsAppNumber {
  waba_id: string;
  waba_name?: string;
  phone_number_id: string;
  display_phone_number?: string;
  verified_name?: string;
}

export interface MetaOtpState {
  otp: { code: string; source: "sms" | "voice"; received_at: number } | null;
  /** Calls the server read this time — 0 almost always. */
  analysed_sessions: number;
}

const base = (clientId: string) =>
  `/api/sessions/v1/${clientId}/connectors/meta`;

export class MetaAPI {
  static getSettings = (clientId: string): Promise<MetaSettings> =>
    api.get<MetaSettings>(`${base(clientId)}/settings`);

  static connectWhatsApp = async (
    clientId: string,
    data: WhatsAppSignupPayload,
  ): Promise<Connector> =>
    (await api.post<{ connector: Connector }, WhatsAppSignupPayload>(
        `${base(clientId)}/whatsapp`,
        data,
      ))
      .connector;

  /** The Meta dialog to send the login popup to, for this tenant and this user. */
  static authorizeUrl = async (
    clientId: string,
    channel: "whatsapp" | "messenger" | "instagram",
  ): Promise<string> =>
    (
      await api.get<{ url: string }>(
        `${base(clientId)}/authorize?channel=${channel}`,
      )
    ).url;

  /** WhatsApp, after the login: the numbers it gave access to. */
  static getWhatsAppNumbers = (
    clientId: string,
    handle: string,
  ): Promise<{ numbers: WhatsAppNumber[] }> =>
    api.get<{ numbers: WhatsAppNumber[] }>(
      `${base(clientId)}/whatsapp/${encodeURIComponent(handle)}/numbers`,
    );

  /** Messenger / Instagram, after the login: the Pages it gave access to. */
  static getPages = (
    clientId: string,
    handle: string,
  ): Promise<{ pages: MetaPage[] }> =>
    api.get<{ pages: MetaPage[] }>(
      `${base(clientId)}/pages/${encodeURIComponent(handle)}`,
    );

  static connectPage = async (
    clientId: string,
    data: {
      handle: string;
      page_id: string;
      channel: "messenger" | "instagram";
      agent_id?: string;
    },
  ): Promise<Connector> =>
    (await api.post<{ connector: Connector }, Record<string, unknown>>(
        `${base(clientId)}/connect`,
        data,
      ))
      .connector;

  /**
   * One look for the code Meta sent to a number nobody can read (SMS or read
   * aloud on a call). Each call keeps the line listening for 15 minutes; `after`
   * is the `received_at` of a code the user did not want, to wait for a newer one.
   */
  static checkOtp = (
    clientId: string,
    number: string,
    after = 0,
  ): Promise<MetaOtpState> =>
    api.get<MetaOtpState>(
      `/api/sessions/v1/${clientId}/otp?number=${encodeURIComponent(number)}&after=${after}`,
    );
}
